import { useState, type ReactNode } from 'react';
import { Lightbulb } from 'lucide-react';
import {
  SUGGESTION_ACTIONS,
  SUGGESTION_ACTION_LABELS,
  SUGGESTION_NOTE_MAX,
  formatSuggestionDecisions,
  type FactorySuggestion,
  type FactorySuggestions,
  type SuggestionAction,
  type SuggestionDecision,
} from '@shared/factory-suggestions';
import { TASK_CHAT_MAX } from '@shared/types/tasks';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { StatusBadge, type StatusTone } from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DecisionItemInput, type DecisionValue } from './DecisionItemInput';
import { FoldingCard } from './FoldingCard';
import { useTaskDraft } from './task-drafts';

/** What each choice does, as its hover text. */
const ACTION_HINTS: Record<SuggestionAction, string> = {
  yes: 'The AI builder builds it on this task’s branch and PR',
  branch: 'A new task for the AI builder, stacked on this task’s branch, with its own PR',
  backlog: 'A task for later: the AI builder makes it from what it knows',
  no: 'Not doing it: recorded as decided',
};

const ACTION_TONES: Record<SuggestionAction, StatusTone> = {
  yes: 'success',
  branch: 'info',
  backlog: 'neutral',
  no: 'neutral',
};

/** The option an item with no options of its own offers: building it as suggested. */
const YES_OPTION = SUGGESTION_ACTION_LABELS.yes;

/** A decision being made: the choice picked and its note. */
export interface SuggestionPick {
  action: SuggestionAction;
  note: string;
}

/**
 * T-55: the software factory's suggestions for further changes on a task (a `factory-suggestions`
 * block, or T-99's decisions block in the wrap phase, in its comment), as a card, copied from the
 * factory's own. Anyone who can comment decides the ones they want to and applies them together, as one
 * comment the factory reads back; decided ones show their decision. On each: pick an option (Yes please,
 * with the option and any steer as its note; an item with no options offers Yes please itself), or New
 * branch, Backlog or Skip. Without `onApply` (a preview) it only lists the suggestions and decisions.
 */
export function FactorySuggestionsCard({
  suggestions,
  decided,
  meta,
  onApply,
  messageRef = null,
  from = 'the AI builder',
  fold = false,
  id,
}: {
  suggestions: FactorySuggestions;
  /** Decisions so far, by suggestion number (1-based). */
  decided?: ReadonlyMap<number, SuggestionDecision>;
  /** Who and when, at the end of the header line. */
  meta?: ReactNode;
  /** Posts the decisions as one comment (formatSuggestionDecisions). */
  onApply?: (message: string) => void;
  /** Names this round in the decisions (decisionsMessageRef), when the task has more than one. */
  messageRef?: string | null;
  /** Who suggested them, in the header. */
  from?: string;
  /** Starts folded to its header line, which opens it (a decided round in the task's Decisions). */
  fold?: boolean;
  /** For a pointer in the comments to scroll to it. */
  id?: string;
}) {
  const [values, setValues] = useState<Record<number, DecisionValue>>({});
  const list = suggestions.suggestions;
  const open = list.filter((_s, i) => !decided?.has(i + 1)).length;
  const decidedCount = list.length - open;
  const chosen = Object.entries(values).flatMap(([n, value]) => {
    const pick = pickOf(value, Boolean(list[Number(n) - 1]?.options?.length));
    return pick && !decided?.has(Number(n)) ? [{ number: Number(n), ...pick }] : [];
  });

  const set = (number: number, change: Partial<DecisionValue>) =>
    setValues((prev) => ({ ...prev, [number]: { ...(prev[number] ?? { picked: [], other: '' }), ...change } }));

  const message = formatSuggestionDecisions(
    chosen.map((p) => ({ ...p, title: list[p.number - 1]?.title ?? '' })),
    messageRef
  );
  // One comment holds them all: past the limit the server would cut it short, so it isn't sent. A note
  // (options picked and a steer) is held to SUGGESTION_NOTE_MAX too, as the factory takes it.
  const tooLong = message.length > TASK_CHAT_MAX || chosen.some((p) => p.note.length > SUGGESTION_NOTE_MAX);
  // Picked and not applied: leaving the page or closing the task asks first.
  const dirty = Boolean(onApply) && chosen.length > 0;
  useUnsavedChangesGuard(dirty, 'Your decisions on the suggestions aren’t sent yet. Leave without sending them?');
  useTaskDraft(dirty);

  const apply = () => {
    if (!onApply || !chosen.length || tooLong) return;
    onApply(message);
    setValues({});
  };

  return (
    <FoldingCard
      id={id}
      fold={fold}
      summary={decidedCount ? decisionsSummary(list.length, decided) : undefined}
      header={
        <>
          <Lightbulb className="size-4" aria-hidden />
          {list.length === 1 ? `A suggestion from ${from}` : `${list.length} suggestions from ${from}`}
          {open === 0 ? <Badge variant="secondary">Decided</Badge> : null}
          {meta ? <span className="ml-auto font-normal">{meta}</span> : null}
        </>
      }
    >
      <ol className="space-y-3">
        {list.map((s, i) => {
          const number = i + 1;
          const decision = decided?.get(number);
          return (
            <li key={s.id} className="space-y-1.5">
              <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                <p className="min-w-0 flex-1 text-sm">
                  <span className="text-muted-foreground">{number}. </span>
                  <span className="font-medium">{s.title}</span>
                </p>
                {s.recommend && !decision ? (
                  <Badge variant="ai" size="sm">
                    Recommends: {SUGGESTION_ACTION_LABELS[s.recommend]}
                  </Badge>
                ) : null}
                {decision ? (
                  <StatusBadge tone={ACTION_TONES[decision.action]} size="sm" dot>
                    {SUGGESTION_ACTION_LABELS[decision.action]}
                  </StatusBadge>
                ) : null}
              </div>
              {s.detail ? <p className="whitespace-pre-wrap break-words pl-4 text-xs text-muted-foreground">{s.detail}</p> : null}
              {/* T-99: what building it, or leaving it out, would mean. */}
              {s.change ? (
                <p className="break-words pl-4 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">If it’s built:</span> {s.change}
                </p>
              ) : null}
              {s.without ? (
                <p className="break-words pl-4 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Without it:</span> {s.without}
                </p>
              ) : null}
              {decision ? (
                decision.note ? (
                  <p className="whitespace-pre-line break-words pl-4 text-xs">
                    <span className="text-muted-foreground">{decision.action === 'no' ? 'Why:' : 'Note:'} </span>
                    {decision.note}
                  </p>
                ) : null
              ) : onApply ? (
                <div className="pl-4">
                  <DecisionItemInput
                    label={s.title}
                    options={itemOptions(s)}
                    multiSelect={s.multiSelect}
                    recommended={s.recommended ?? (s.recommend === 'yes' && !s.options?.length ? YES_OPTION : undefined)}
                    value={values[number]}
                    // The note carries the options picked too, so the box leaves them room.
                    maxLength={Math.max(0, SUGGESTION_NOTE_MAX - (values[number]?.picked.join('; ').length ?? 0) - 2)}
                    actions={{ skip: true, backlog: true, branch: true }}
                    hints={{ skip: ACTION_HINTS.no, backlog: ACTION_HINTS.backlog, branch: ACTION_HINTS.branch }}
                    skipNote
                    placeholder={s.options?.length ? 'Other, or steer it: “this, but …”' : 'Or say how: “yes, but …”, or another way'}
                    onChange={(change) => set(number, change)}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
      {onApply && open > 0 ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {tooLong ? (
            <span className="text-xs text-destructive">Too long: shorten your notes</span>
          ) : (
            <span className="text-xs text-muted-foreground">
              {chosen.length ? `${chosen.length} of ${open} picked` : 'Pick an option, steer, New branch, Backlog or Skip on each'}
            </span>
          )}
          <Button size="sm" disabled={!chosen.length || tooLong} onClick={apply}>
            Apply
          </Button>
        </div>
      ) : null}
    </FoldingCard>
  );
}

/** An item's options to pick from: its own, or Yes please alone. */
function itemOptions(s: FactorySuggestion) {
  return s.options?.length ? s.options : [{ label: YES_OPTION, description: ACTION_HINTS.yes }];
}

/**
 * T-99: what's picked on a suggestion as the decision it applies (as the factory's card does): Skip,
 * Backlog and New branch with the box as their note; an option or words are Yes please, its note the
 * options picked (not the bare Yes please) and the steer. Null when nothing is picked yet.
 */
function pickOf(value: DecisionValue, ownOptions: boolean): SuggestionPick | null {
  const other = value.other.trim();
  if (value.choice === 'skip') return { action: 'no', note: other };
  if (value.choice) return { action: value.choice, note: other };
  // The bare Yes please card isn't repeated in the note; an option of the item's own named that is.
  const picked = ownOptions ? value.picked : value.picked.filter((p) => p !== YES_OPTION);
  if (!value.picked.length && !other) return null;
  return { action: 'yes', note: [...picked, ...(other ? [other] : [])].join('; ') };
}

/** What was decided, on one line for a folded round: "1 Yes please · 1 Skip". */
function decisionsSummary(total: number, decided?: ReadonlyMap<number, SuggestionDecision>): string | undefined {
  if (!decided?.size) return undefined;
  const counts = new Map<SuggestionAction, number>();
  for (const d of decided.values()) counts.set(d.action, (counts.get(d.action) ?? 0) + 1);
  const parts = SUGGESTION_ACTIONS.filter((a) => counts.has(a)).map((a) => `${counts.get(a)} ${SUGGESTION_ACTION_LABELS[a]}`);
  const left = total - decided.size;
  return [...parts, ...(left > 0 ? [`${left} to decide`] : [])].join(' · ');
}

/** In the comments, the factory's suggestions as one line pointing to the task's Decisions, where they're decided. */
export function FactorySuggestionsPointer({
  count,
  decided = false,
  onShow,
}: {
  count: number;
  decided?: boolean;
  /** Scrolls to the suggestions (showFoldingCard). */
  onShow: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
      <Lightbulb className="size-4 shrink-0" aria-hidden />
      <span>{count === 1 ? 'Suggested a further change' : `Suggested ${count} further changes`}</span>
      {decided ? <Badge variant="secondary">Decided</Badge> : null}
      <Button variant="link" size="inline" onClick={onShow}>
        {decided ? 'See Decisions' : 'Decide under the description'}
      </Button>
    </div>
  );
}
